import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';
import config from '../config';
import { colors } from '../theme/designSystem';

const touchTargetStyles = {
    '& .MuiInputBase-root': {
        minHeight: 44,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.neutral[300],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary[400],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary[600],
        borderWidth: 2,
    },
};

const buildAllLeaguesOption = () => ({ label: 'All Leagues', value: 'all' });
const LEAGUE_LABEL_ALIASES = {
    IPL: 'Indian Premier League',
    BBL: 'Big Bash League',
    PSL: 'Pakistan Super League',
    CPL: 'Caribbean Premier League',
    SA20: 'SA20',
};

const normalizeLeagueToken = (value) => String(value || '').trim().toLowerCase();

const CompetitionFilter = ({ onFilterChange, isMobile, value }) => {
    const [includeInternational, setIncludeInternational] = useState(value?.international ?? false);
    const [topTeams, setTopTeams] = useState(value?.topTeams ?? 10);
    // What is typed, kept separately from the committed number. Clearing the box to type "20"
    // used to parseInt('') -> NaN, which was stored, sent upstream and echoed back, so the field
    // blanked itself and fought the user's typing.
    const [topTeamsDraft, setTopTeamsDraft] = useState(String(value?.topTeams ?? 10));
    useEffect(() => {
        setTopTeamsDraft(String(topTeams));
    }, [topTeams]);
    const [selectedLeagues, setSelectedLeagues] = useState([buildAllLeaguesOption()]);
    const [leagues, setLeagues] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCompetitions = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${config.API_URL}/competitions`);
                setLeagues(response.data.leagues);
            } catch (error) {
                setError('Failed to load competitions');
            } finally {
                setLoading(false);
            }
        };
        fetchCompetitions();
    }, []);

    useEffect(() => {
        if (!leagues.length) return;

        // When "All Leagues" is selected (empty array or all leagues), send empty array to backend
        // This prevents the URL from becoming too long with 50+ league parameters
        const allLeagueValues = leagues.map((league) => league.value);
        const isAllLeagues = !value?.leagues?.length ||
            (value.leagues.length === allLeagueValues.length &&
             value.leagues.every((league) => allLeagueValues.includes(league)));

        const resolvedFilters = {
            leagues: isAllLeagues ? [] : value.leagues,
            international: value?.international ?? false,
            topTeams: Number.isInteger(value?.topTeams) ? value.topTeams : 10,
        };

        const optionsByValue = new Map(leagues.map((league) => [league.value, league]));
        const optionsByLabel = new Map(leagues.map((league) => [normalizeLeagueToken(league.label), league]));
        const optionsByNormalizedValue = new Map(leagues.map((league) => [normalizeLeagueToken(league.value), league]));
        const resolveLeagueOption = (leagueKey) => {
            if (!leagueKey) return null;
            if (optionsByValue.has(leagueKey)) return optionsByValue.get(leagueKey);
            const normalized = normalizeLeagueToken(leagueKey);
            if (optionsByNormalizedValue.has(normalized)) return optionsByNormalizedValue.get(normalized);
            if (optionsByLabel.has(normalized)) return optionsByLabel.get(normalized);
            const aliasLabel = LEAGUE_LABEL_ALIASES[leagueKey];
            if (aliasLabel && optionsByLabel.has(normalizeLeagueToken(aliasLabel))) {
                return optionsByLabel.get(normalizeLeagueToken(aliasLabel));
            }
            return null;
        };
        const nextSelectedLeagues = isAllLeagues
            ? [buildAllLeaguesOption()]
            : resolvedFilters.leagues
                  .map((league) => resolveLeagueOption(league))
                  .filter(Boolean);

        setSelectedLeagues(nextSelectedLeagues);
        setIncludeInternational(resolvedFilters.international);
        setTopTeams(resolvedFilters.topTeams);

        const leaguesEqual = Array.isArray(value?.leagues)
            && value.leagues.length === resolvedFilters.leagues.length
            && value.leagues.every((league) => resolvedFilters.leagues.includes(league));
        const internationalEqual = value?.international === resolvedFilters.international;
        const topTeamsEqual = value?.topTeams === resolvedFilters.topTeams;

        if (!leaguesEqual || !internationalEqual || !topTeamsEqual) {
            onFilterChange(resolvedFilters);
        }
    }, [leagues, value, onFilterChange]);

    // Handle selection changes including "All Leagues"
    const handleSelectionChange = (newLeagues, newInternational, newTopTeams) => {
        // Check if "All Leagues" is selected
        const hasAllLeagues = newLeagues?.some(league => league.value === 'all');

        // If "All Leagues" is selected, send empty array (backend interprets as all leagues)
        // This prevents the URL from becoming too long with 50+ league parameters
        const leaguesToSend = hasAllLeagues
            ? []
            : (newLeagues?.map(league => league.value) || []);

        onFilterChange({
            leagues: leaguesToSend,
            international: newInternational,
            topTeams: newTopTeams
        });
    };

    // Handle Autocomplete change
    const handleAutocompleteChange = (event, newValue) => {
        // "All Leagues" and specific leagues are exclusive, decided by what was just picked (MUI
        // appends it last). This used to collapse to "All Leagues" whenever the chip was still in
        // the list, so adding IPL next to it was swallowed and a single league could never be
        // chosen; removing the chip left an empty list, which also synced back to "All".
        const picked = newValue?.[newValue.length - 1];
        let updatedSelection;
        if (!newValue?.length || picked?.value === 'all') {
            updatedSelection = [buildAllLeaguesOption()];
        } else {
            updatedSelection = newValue.filter((league) => league.value !== 'all');
        }

        setSelectedLeagues(updatedSelection);
        handleSelectionChange(updatedSelection, includeInternational, topTeams);
    };

    if (loading) return <CircularProgress size={20} />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box sx={{ mb: 2 }}>
            <FormGroup sx={{ flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: 'flex-start' }}>
                <Autocomplete
                    multiple
                    value={selectedLeagues}
                    onChange={handleAutocompleteChange}
                    size="medium"
                    options={[
                        {
                            label: 'All Leagues',
                            value: 'all'
                        },
                        ...leagues
                    ]}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Leagues" sx={touchTargetStyles} />
                    )}
                    sx={{ width: isMobile ? '100%' : 400, mb: isMobile ? 1 : 0, mr: isMobile ? 0 : 2 }}
                />
                <FormControlLabel
                    sx={{ minHeight: 44 }}
                    control={
                        <Checkbox
                            checked={includeInternational}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                setIncludeInternational(newValue);
                                handleSelectionChange(selectedLeagues, newValue, topTeams);
                            }}
                            sx={{
                                p: 1.5,
                                '&:focus-visible': {
                                    outline: `2px solid ${colors.primary[600]}`,
                                    outlineOffset: 2,
                                },
                            }}
                        />
                    }
                    label="Include International Matches"
                />
                {includeInternational && (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 0 }}>
                        <TextField
                            type="number"
                            label="Top Teams"
                            value={topTeamsDraft}
                            onChange={(e) => {
                                const raw = e.target.value;
                                setTopTeamsDraft(raw);
                                const newValue = Number.parseInt(raw, 10);
                                if (Number.isInteger(newValue) && newValue >= 1 && newValue <= 20 && newValue !== topTeams) {
                                    setTopTeams(newValue);
                                    handleSelectionChange(selectedLeagues, includeInternational, newValue);
                                }
                            }}
                            onBlur={() => {
                                const newValue = Number.parseInt(topTeamsDraft, 10);
                                if (!(Number.isInteger(newValue) && newValue >= 1 && newValue <= 20)) {
                                    setTopTeamsDraft(String(topTeams));
                                }
                            }}
                            helperText={isMobile ? '1-20' : undefined}
                            inputProps={{ min: 1, max: 20, inputMode: 'numeric', pattern: '[0-9]*' }}
                            sx={{
                                width: isMobile ? '100%' : 100,
                                ...touchTargetStyles,
                            }}
                            fullWidth={isMobile}
                            size="medium"
                        />
                    </Box>
                )}
            </FormGroup>
        </Box>
    );
};

export default CompetitionFilter;
